import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { FormField } from "@/components/common/form-field";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import { SelectField } from "@/features/clients/components/record-form-fields";
import { getWorksheet, lawsuitsApi, patchWorksheet } from "@/features/worksheets/api";
import { AutosaveForm } from "@/features/worksheets/components/autosave-form";
import { CollectionCard } from "@/features/worksheets/components/collection-card";
import { LawsuitDialog } from "@/features/worksheets/components/dialogs/lawsuit-dialog";
import { MoneyField } from "@/features/worksheets/components/money-field";
import { useCollection } from "@/features/worksheets/hooks/use-collection";
import { centsToDollars, formatMoney } from "@/features/worksheets/money";
import { type Lawsuit, SWITCH_OPTIONS, type Switch, type Worksheet } from "@/features/worksheets/types";

const MONEY_FIELDS = ["trustAmount", "fundsHeldAmount", "safeDepositValue"] as const;

const BANKRUPTCY_TYPE_OPTIONS = [
  { value: "D", label: "Chapter 7 (discharge)" },
  { value: "C", label: "Chapter 13 (reorganization)" },
];

interface FinancialValues {
  lawsuitSw: Switch;
  bankruptNowSw: Switch;
  bankruptSw: Switch;
  bankruptcyType: "" | "D" | "C";
  bankruptcyFiled: string;
  bankruptcyDismissed: string;
  bankruptcyPetition: string;
  bankruptcyLocation: string;
  benefitSw: Switch;
  trusteeSw: Switch;
  trustName: string;
  trustAmount: string;
  trustDate: string;
  trustTrustee: string;
  fundsHeldSw: Switch;
  fundsHeldAmount: string;
  fundsHeldLocation: string;
  safeDepSw: Switch;
  safeDepositName: string;
  safeDepositAddress: string;
  safeDepositBox: string;
  safeDepositContent: string;
  safeDepositValue: string;
  abroadSw: Switch;
  abroadFrom: string;
  abroadTo: string;
  nonusSw: Switch;
  nonusAssetDesc: string;
}

function toInitialValues(w: Worksheet): FinancialValues {
  return {
    lawsuitSw: w.lawsuitSw,
    bankruptNowSw: w.bankruptNowSw,
    bankruptSw: w.bankruptSw,
    bankruptcyType: w.bankruptcyType,
    bankruptcyFiled: w.bankruptcyFiled ?? "",
    bankruptcyDismissed: w.bankruptcyDismissed ?? "",
    bankruptcyPetition: w.bankruptcyPetition,
    bankruptcyLocation: w.bankruptcyLocation,
    benefitSw: w.benefitSw,
    trusteeSw: w.trusteeSw,
    trustName: w.trustName,
    trustAmount: centsToDollars(w.trustAmount),
    trustDate: w.trustDate ?? "",
    trustTrustee: w.trustTrustee,
    fundsHeldSw: w.fundsHeldSw,
    fundsHeldAmount: centsToDollars(w.fundsHeldAmount),
    fundsHeldLocation: w.fundsHeldLocation,
    safeDepSw: w.safeDepSw,
    safeDepositName: w.safeDepositName,
    safeDepositAddress: w.safeDepositAddress,
    safeDepositBox: w.safeDepositBox,
    safeDepositContent: w.safeDepositContent,
    safeDepositValue: centsToDollars(w.safeDepositValue),
    abroadSw: w.abroadSw,
    abroadFrom: w.abroadFrom ?? "",
    abroadTo: w.abroadTo ?? "",
    nonusSw: w.nonusSw,
    nonusAssetDesc: w.nonusAssetDesc,
  };
}

/** A titled Card section wrapper. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="gap-4">
      <div className="px-6">
        <h3 className="font-semibold">{title}</h3>
      </div>
      <div className="grid gap-4 px-6">{children}</div>
    </Card>
  );
}

export function FinancialTab({ caseId }: { caseId: string }) {
  const [worksheet, setWorksheet] = useState<Worksheet | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getWorksheet(caseId)
      .then((w) => active && setWorksheet(w))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [caseId]);

  if (loading || !worksheet) {
    return <p className="text-muted-foreground text-sm">Loading…</p>;
  }

  return (
    <div className="grid gap-6">
      <LawsuitsSection caseId={caseId} lawsuitSw={worksheet.lawsuitSw} />

      <Formik initialValues={toInitialValues(worksheet)} onSubmit={() => {}}>
        {({ values }) => (
          <Form className="grid gap-6">
            <AutosaveForm
              save={(patch) => patchWorksheet(caseId, patch)}
              moneyFields={MONEY_FIELDS}
            />

            <Section title="Bankruptcy">
              <div className="grid gap-4 sm:grid-cols-2">
                <SelectField
                  name="bankruptNowSw"
                  label="Currently in bankruptcy?"
                  options={SWITCH_OPTIONS}
                />
                <SelectField
                  name="bankruptSw"
                  label="Ever filed bankruptcy?"
                  options={SWITCH_OPTIONS}
                />
              </div>
              {(values.bankruptNowSw === "Y" || values.bankruptSw === "Y") && (
                <>
                  <Separator />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <SelectField
                      name="bankruptcyType"
                      label="Type"
                      options={BANKRUPTCY_TYPE_OPTIONS}
                    />
                    <FormField
                      name="bankruptcyPetition"
                      label="Petition No."
                      labelClassName={FIELD_LABEL}
                    />
                    <FormField
                      name="bankruptcyFiled"
                      label="Date Filed"
                      labelClassName={FIELD_LABEL}
                      type="date"
                    />
                    <FormField
                      name="bankruptcyDismissed"
                      label="Date Dismissed"
                      labelClassName={FIELD_LABEL}
                      type="date"
                    />
                    <FormField
                      name="bankruptcyLocation"
                      label="Location Filed"
                      labelClassName={FIELD_LABEL}
                    />
                  </div>
                </>
              )}
            </Section>

            <Section title="Benefit from Bankruptcy">
              <SelectField
                name="benefitSw"
                label="Beneficiary of a bankruptcy?"
                options={SWITCH_OPTIONS}
              />
            </Section>

            <Section title="Trust">
              <SelectField
                name="trusteeSw"
                label="Beneficiary or trustee of a trust?"
                options={SWITCH_OPTIONS}
              />
              {values.trusteeSw === "Y" && (
                <>
                  <Separator />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField name="trustName" label="Trust Name" labelClassName={FIELD_LABEL} />
                    <MoneyField name="trustAmount" label="Amount" />
                    <FormField
                      name="trustDate"
                      label="Date Established"
                      labelClassName={FIELD_LABEL}
                      type="date"
                    />
                    <FormField
                      name="trustTrustee"
                      label="Trustee"
                      labelClassName={FIELD_LABEL}
                    />
                  </div>
                </>
              )}
            </Section>

            <Section title="Funds Held for You">
              <SelectField
                name="fundsHeldSw"
                label="Are funds held on your behalf?"
                options={SWITCH_OPTIONS}
              />
              {values.fundsHeldSw === "Y" && (
                <>
                  <Separator />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <MoneyField name="fundsHeldAmount" label="Amount" />
                    <FormField
                      name="fundsHeldLocation"
                      label="Location / Holder"
                      labelClassName={FIELD_LABEL}
                    />
                  </div>
                </>
              )}
            </Section>

            <Section title="Safe Deposit Box">
              <SelectField
                name="safeDepSw"
                label="Have a safe-deposit box?"
                options={SWITCH_OPTIONS}
              />
              {values.safeDepSw === "Y" && (
                <>
                  <Separator />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      name="safeDepositName"
                      label="Institution Name"
                      labelClassName={FIELD_LABEL}
                    />
                    <FormField
                      name="safeDepositBox"
                      label="Box No."
                      labelClassName={FIELD_LABEL}
                    />
                    <FormField
                      name="safeDepositAddress"
                      label="Address"
                      labelClassName={FIELD_LABEL}
                    />
                    <MoneyField name="safeDepositValue" label="Contents Value" />
                    <FormField
                      name="safeDepositContent"
                      label="Contents"
                      labelClassName={FIELD_LABEL}
                    />
                  </div>
                </>
              )}
            </Section>

            <Section title="Assets Abroad">
              <SelectField
                name="abroadSw"
                label="Lived or worked abroad?"
                options={SWITCH_OPTIONS}
              />
              {values.abroadSw === "Y" && (
                <>
                  <Separator />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      name="abroadFrom"
                      label="From"
                      labelClassName={FIELD_LABEL}
                      type="date"
                    />
                    <FormField
                      name="abroadTo"
                      label="To"
                      labelClassName={FIELD_LABEL}
                      type="date"
                    />
                  </div>
                </>
              )}
            </Section>

            <Section title="Non-US Assets">
              <SelectField
                name="nonusSw"
                label="Hold assets outside the US?"
                options={SWITCH_OPTIONS}
              />
              {values.nonusSw === "Y" && (
                <>
                  <Separator />
                  <FormField
                    name="nonusAssetDesc"
                    label="Description"
                    labelClassName={FIELD_LABEL}
                  />
                </>
              )}
            </Section>
          </Form>
        )}
      </Formik>
    </div>
  );
}

/**
 * Lawsuits list — gated by the header's `lawsuitSw`. The switch is its own tiny
 * Formik+autosave so the collection card can live outside the main header form.
 */
function LawsuitsSection({ caseId, lawsuitSw }: { caseId: string; lawsuitSw: Switch }) {
  const { rows, loading, reload } = useCollection<Lawsuit>(caseId, lawsuitsApi.list);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Lawsuit | null>(null);

  return (
    <Card className="gap-4">
      <div className="px-6">
        <h3 className="font-semibold">Lawsuits</h3>
      </div>
      <div className="px-6">
        <Formik initialValues={{ lawsuitSw }} onSubmit={() => {}}>
          <Form>
            <AutosaveForm save={(patch) => patchWorksheet(caseId, patch)} />
            <div className="max-w-xs">
              <SelectField
                name="lawsuitSw"
                label="Party to any lawsuit?"
                options={SWITCH_OPTIONS}
              />
            </div>
          </Form>
        </Formik>
      </div>
      <CollectionCard<Lawsuit>
        title="Lawsuits"
        addLabel="Add Lawsuit"
        loading={loading}
        rows={rows}
        columns={[
          { header: "Description", cell: (r) => r.description },
          { header: "Amount", cell: (r) => formatMoney(r.amount) },
          { header: "Date Filed", cell: (r) => r.dateFiled ?? "—" },
        ]}
        onAdd={() => {
          setEditing(null);
          setDialogOpen(true);
        }}
        onEdit={(r) => {
          setEditing(r);
          setDialogOpen(true);
        }}
        onDelete={async (r) => {
          await lawsuitsApi.remove(caseId, r.id);
          reload();
        }}
      />
      <LawsuitDialog
        caseId={caseId}
        record={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={reload}
      />
    </Card>
  );
}
